import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './ProjectContract.css';
import lpApi from '../../helpers/lpApi';
import ProjectMilestones from '../ProjectMilestones/ProjectMilestones';
import LpDepositCheckout from '../ProjectPayment/LpDepositCheckout';
import LpCardOnFile from '../ProjectPayment/LpCardOnFile';

// =============================================================================
// ProjectContract — shared CONTRACT + e-signature view for the Custom Projects
// (Gros Projets) module, used by BOTH the client (MyProjects, after accepting a
// quote) and the company (CompanyProjects, after being selected). Step #4 of the
// client -> company -> accept -> contract loop.
//
// All backend ops live on besthand-api and are reached through the `/lp-graphql`
// proxy via the `lpApi` helper (which forwards the logged-in id_token cookie).
// No contract/signature logic is duplicated here — the api is the single source
// of truth. The mutations REUSED, unchanged, are:
//   - prepareContract(projectQuoteId)         -> materializes the DRAFT contract
//                                                (idempotent; either party may call)
//   - requestSignatureOtp(contractId, party)  -> 6-digit SMS OTP, 10-min TTL
//   - signContract(contractId, party, otp,
//       consentToElectronicSignature, scrolledToEnd) -> ESIGN/UETA signature
//   - cancelContractDuringWindow(contractId, reason) -> client right-of-rescission
// and the queries contract(contractId) + quote(quoteId) for the agreed scope,
// line items, total and milestone schedule.
//
// SIGNING (api signContract): either party may sign first. The contract becomes
// FULLY_SIGNED only when both have signed, then enters IN_CANCELLATION_PERIOD
// until cancellationDeadline, after which a cron flips it to ACTIVE. We surface
// each party's signature status (signedAtClient / signedAtCompany) and the
// cancellation window directly from the contract row.
//
// PDF: contract.pdfUrl is an on-disk path on besthand-api, not a public URL. We
// link to a same-origin web proxy (/lp-contract-pdf/:id) that re-attaches the
// id_token cookie server-side and streams the gated PDF from the api — mirroring
// the /lp-graphql proxy. No token ever appears in the URL.
//
// SSR-safe: no window/document access at module load or render; all of it lives
// in lifecycle (componentDidMount) and click handlers.
// =============================================================================

// ---- GraphQL operations (against besthand-api ProjectsResponseType) ----

const M_PREPARE_CONTRACT = `
  mutation ($projectQuoteId: String!) {
    prepareContract(projectQuoteId: $projectQuoteId) {
      status errorMessage
      contract {
        id projectRequestId projectQuoteId clientUserId companyId status
        totalAmount downPaymentAmount currency
        cancellationDeadline cancellationDaysApplied
        signedAtClient signedAtCompany
        depositPaidAt depositPaymentIntentId
        cancelledAt cancellationReason createdAt
      }
    }
  }
`;

const Q_CONTRACT = `
  query ($contractId: String!) {
    contract(contractId: $contractId) {
      status errorMessage
      contract {
        id projectRequestId projectQuoteId clientUserId companyId status
        totalAmount downPaymentAmount currency
        cancellationDeadline cancellationDaysApplied
        signedAtClient signedAtCompany
        depositPaidAt depositPaymentIntentId
        cancelledAt cancellationReason createdAt
      }
    }
  }
`;

const Q_QUOTE = `
  query ($quoteId: String!) {
    quote(quoteId: $quoteId) {
      status errorMessage
      quote {
        id status total subtotal platformCommissionClient visitFee currency
        laborItemsJson materialsItemsJson milestonesProposalJson
        warrantyDurationMonths warrantyScope totalDurationDays
      }
    }
  }
`;

const M_REQUEST_OTP = `
  mutation ($contractId: String!, $party: String!) {
    requestSignatureOtp(contractId: $contractId, party: $party) {
      status errorMessage
    }
  }
`;

const M_SIGN = `
  mutation (
    $contractId: String!, $party: String!, $otp: String!,
    $consentToElectronicSignature: Boolean!, $scrolledToEnd: Boolean!
  ) {
    signContract(
      contractId: $contractId, party: $party, otp: $otp,
      consentToElectronicSignature: $consentToElectronicSignature, scrolledToEnd: $scrolledToEnd
    ) {
      status errorMessage
      contract {
        id status totalAmount downPaymentAmount currency
        cancellationDeadline cancellationDaysApplied
        signedAtClient signedAtCompany cancelledAt cancellationReason
        depositPaidAt depositPaymentIntentId
      }
    }
  }
`;

const M_CANCEL = `
  mutation ($contractId: String!, $reason: String) {
    cancelContractDuringWindow(contractId: $contractId, reason: $reason) {
      status errorMessage
      contract { id status cancelledAt cancellationReason }
    }
  }
`;

// ---- helpers ----

function money2(n, currency) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const cur = currency || 'USD';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${cur && cur !== 'USD' ? ` ${cur}` : ''}`;
}

function safeParse(json) {
  if (!json) return null;
  try { return typeof json === 'string' ? JSON.parse(json) : json; } catch (e) { return null; }
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function lineAmount(li) {
  if (li.lineTotal != null) return Number(li.lineTotal);
  if (li.amount != null) return Number(li.amount);
  return (Number(li.qty) || 1) * (Number(li.unitPrice) || 0);
}

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Reuse the exact inline path vocabulary already used across the Custom Projects
// web surfaces (MyProjects + CompanyProjects ICONS) — none invented.
const ICONS = {
  back: 'M15 18l-6-6 6-6',
  doc: 'M14 3v5h5 M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5Z',
  check: 'M20 6 9 17l-5-5',
  shield: 'M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4Z',
  clock: 'M12 7v5l3 2 M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
  wallet: 'M3 10h18M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z',
};

class ProjectContract extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: '',
      contract: null,
      quote: null,

      // signature flow (this viewer's party)
      consent: false,
      reviewed: false, // attests they reviewed the full agreement / PDF (intent)
      otpPhase: 'idle', // 'idle' -> 'otpSent'
      otp: '',
      sending: false,
      signing: false,
      signError: '',
      signNotice: '',

      // cancellation (client only)
      cancelOpen: false,
      cancelReason: '',
      cancelling: false,
    };
  }

  componentDidMount() {
    this.bootstrap();
  }

  // eslint-disable-next-line class-methods-use-this
  firstError(res) {
    if (res && res.errors && res.errors.length) return res.errors[0].message;
    return 'Something went wrong.';
  }

  get party() {
    return this.props.viewerRole === 'COMPANY' ? 'COMPANY' : 'CLIENT';
  }

  async bootstrap() {
    this.setState({ loading: true, error: '' });
    try {
      // 1) Ensure the contract exists. prepareContract is idempotent — it returns
      //    the existing contract if one was already prepared for this quote — and
      //    either party may call it. This avoids a separate "create" step and
      //    works whichever side opens the contract first.
      let contract = null;
      if (this.props.projectQuoteId) {
        const pc = await lpApi(M_PREPARE_CONTRACT, { projectQuoteId: String(this.props.projectQuoteId) });
        const env = pc && pc.data && pc.data.prepareContract;
        if (pc.errors || !env || (env.status && env.status >= 400)) {
          this.setState({ loading: false, error: (env && env.errorMessage) || this.firstError(pc) });
          return;
        }
        contract = env.contract;
      } else if (this.props.contractId) {
        const cr = await lpApi(Q_CONTRACT, { contractId: String(this.props.contractId) });
        const env = cr && cr.data && cr.data.contract;
        if (cr.errors || !env || (env.status && env.status >= 400)) {
          this.setState({ loading: false, error: (env && env.errorMessage) || this.firstError(cr) });
          return;
        }
        contract = env.contract;
      }
      if (!contract) {
        this.setState({ loading: false, error: 'Contract is not available yet.' });
        return;
      }

      // 2) Load the agreed quote for scope / line items / milestone schedule.
      let quote = null;
      if (contract.projectQuoteId) {
        const qr = await lpApi(Q_QUOTE, { quoteId: String(contract.projectQuoteId) });
        const qEnv = qr && qr.data && qr.data.quote;
        if (qEnv && qEnv.status === 200) quote = qEnv.quote;
      }

      this.setState({ loading: false, contract, quote });
    } catch (e) {
      this.setState({ loading: false, error: `Could not load the contract: ${e.message}` });
    }
  }

  async reloadContract() {
    const id = this.state.contract && this.state.contract.id;
    if (!id) return;
    const cr = await lpApi(Q_CONTRACT, { contractId: String(id) });
    const env = cr && cr.data && cr.data.contract;
    if (env && env.status === 200 && env.contract) {
      this.setState({ contract: env.contract });
    }
  }

  // ---- e-signature flow ----

  canSendOtp() {
    return this.state.consent && this.state.reviewed && !this.state.sending;
  }

  async sendOtp() {
    if (!this.canSendOtp()) return;
    const id = this.state.contract && this.state.contract.id;
    this.setState({ sending: true, signError: '', signNotice: '' });
    try {
      const res = await lpApi(M_REQUEST_OTP, { contractId: String(id), party: this.party });
      const env = res && res.data && res.data.requestSignatureOtp;
      if (res.errors || !env || env.status !== 200) {
        this.setState({ sending: false, signError: (env && env.errorMessage) || this.firstError(res) });
        return;
      }
      this.setState({ sending: false, otpPhase: 'otpSent', signNotice: 'We texted a 6-digit signing code to the phone on file.' });
    } catch (e) {
      this.setState({ sending: false, signError: `Could not send the code: ${e.message}` });
    }
  }

  async sign() {
    const { consent, reviewed, otp } = this.state;
    const id = this.state.contract && this.state.contract.id;
    if (!consent || !reviewed) {
      this.setState({ signError: 'Please confirm the consent items before signing.' });
      return;
    }
    if ((otp || '').replace(/\D/g, '').length < 4) {
      this.setState({ signError: 'Enter the verification code we texted you.' });
      return;
    }
    this.setState({ signing: true, signError: '', signNotice: '' });
    try {
      const res = await lpApi(M_SIGN, {
        contractId: String(id),
        party: this.party,
        otp: String(otp).trim(),
        consentToElectronicSignature: true, // ESIGN consent (pillar 1)
        scrolledToEnd: true, // intent — gated client-side by `reviewed` (pillar 2)
      });
      const env = res && res.data && res.data.signContract;
      if (res.errors || !env || (env.status && env.status >= 400) || !env.contract) {
        this.setState({ signing: false, signError: (env && env.errorMessage) || this.firstError(res) });
        return;
      }
      this.setState({
        signing: false,
        otpPhase: 'idle',
        otp: '',
        signNotice: 'Your signature has been recorded.',
        contract: { ...this.state.contract, ...env.contract },
      });
    } catch (e) {
      this.setState({ signing: false, signError: `We couldn’t record your signature: ${e.message}` });
    }
  }

  // ---- cancellation (client right of rescission) ----

  async confirmCancel() {
    const id = this.state.contract && this.state.contract.id;
    this.setState({ cancelling: true, signError: '' });
    try {
      const res = await lpApi(M_CANCEL, { contractId: String(id), reason: this.state.cancelReason || null });
      const env = res && res.data && res.data.cancelContractDuringWindow;
      if (res.errors || !env || (env.status && env.status >= 400) || !env.contract) {
        this.setState({ cancelling: false, signError: (env && env.errorMessage) || this.firstError(res) });
        return;
      }
      this.setState({ cancelling: false, cancelOpen: false });
      this.reloadContract();
    } catch (e) {
      this.setState({ cancelling: false, signError: `Could not cancel: ${e.message}` });
    }
  }

  // ---- derived ----

  pdfHref() {
    // Same-origin web proxy that streams the gated PDF from besthand-api with the
    // id_token re-attached server-side (mirrors /lp-graphql). No token in the URL.
    const id = this.state.contract && this.state.contract.id;
    return id ? `/lp-contract-pdf/${id}` : null;
  }

  // ---- render ----

  render() {
    const { loading, error, contract } = this.state;
    return (
      <div className={s.contract}>
        {this.props.onBack && (
          <button type="button" className={s.backBtn} onClick={this.props.onBack}>
            <Icon d={ICONS.back} /> {this.props.backLabel || 'Back'}
          </button>
        )}
        {loading ? (
          <div className={s.loading}>Loading your contract…</div>
        ) : error ? (
          <div className={s.alertErr}>{error}</div>
        ) : contract ? (
          this.renderContract()
        ) : (
          <div className={s.alertErr}>Contract is not available yet.</div>
        )}
      </div>
    );
  }

  renderHeadStatus() {
    const { contract } = this.state;
    const LABEL = {
      DRAFT: 'Awaiting signatures',
      SIGNED_CLIENT: 'Awaiting company signature',
      SIGNED_COMPANY: 'Awaiting your signature',
      FULLY_SIGNED: 'Fully signed',
      IN_CANCELLATION_PERIOD: 'Signed · cancellation window',
      ACTIVE: 'Active',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
    };
    const cls = s[`st_${(contract.status || '').toLowerCase()}`] || '';
    return (
      <span className={`${s.statusPill} ${cls}`}>
        <Icon d={ICONS.shield} /> {LABEL[contract.status] || contract.status}
      </span>
    );
  }

  renderContract() {
    const { contract, quote } = this.state;
    const labor = safeParse(quote && quote.laborItemsJson) || [];
    const materials = safeParse(quote && quote.materialsItemsJson) || [];
    const lineItems = [
      ...(Array.isArray(labor) ? labor : []),
      ...(Array.isArray(materials) ? materials : []),
    ];
    const milestones = safeParse(quote && quote.milestonesProposalJson) || [];
    const currency = contract.currency || (quote && quote.currency);
    const fullySigned = ['FULLY_SIGNED', 'IN_CANCELLATION_PERIOD', 'ACTIVE', 'COMPLETED'].includes(contract.status);
    const cancelled = contract.status === 'CANCELLED';

    return (
      <div>
        <div className={s.head}>
          <div>
            <span className={s.eyebrow}>BestHand Large Projects · contract</span>
            <h1 className={s.title}>{this.props.projectTitle || 'Project contract'}</h1>
            <p className={s.subtitle}>
              {this.props.viewerRole === 'COMPANY'
                ? 'Review the agreed scope and terms, then sign to lock in the project.'
                : 'Review the agreement your selected company prepared, then sign to move into escrow.'}
            </p>
          </div>
          {this.renderHeadStatus()}
        </div>

        {/* Agreed total + escrow deposit */}
        <div className={s.card}>
          <p className={s.cardTitle}><Icon d={ICONS.wallet} /> Contract total</p>
          <div className={s.totalLine}>
            <span>Project total</span>
            <span>{money2(contract.totalAmount, currency)}</span>
          </div>
          <div className={`${s.totalLine} ${s.totalGrand}`}>
            <span>Escrow deposit (held, not released)</span>
            <span>{money2(contract.downPaymentAmount, currency)}</span>
          </div>
          <p className={s.depositNote}>
            Your payment is held in escrow and released milestone by milestone as work is approved —
            no money moves until you accept each milestone.
          </p>
        </div>

        {/* Agreed scope = quote line items */}
        {lineItems.length > 0 && (
          <div className={s.card}>
            <p className={s.cardTitle}><Icon d={ICONS.doc} /> Agreed scope &amp; line items</p>
            {lineItems.map((li, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={s.lineRow} key={i}>
                <span className={s.lineDesc}>
                  {li.description || li.name || 'Item'}
                  {li.qty != null && li.qty !== '' ? ` ×${li.qty}` : ''}
                </span>
                <span className={s.lineAmt}>{money2(lineAmount(li), currency)}</span>
              </div>
            ))}
            <div className={s.totalsRow}>
              {quote && quote.subtotal != null && (
                <div className={s.totalLine}><span>Subtotal</span><span>{money2(quote.subtotal, currency)}</span></div>
              )}
              {quote && quote.platformCommissionClient != null && (
                <div className={s.totalLine}><span>Platform fee</span><span>{money2(quote.platformCommissionClient, currency)}</span></div>
              )}
              <div className={`${s.totalLine} ${s.totalGrand}`}>
                <span>Total</span><span>{money2(contract.totalAmount, currency)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Milestone schedule */}
        {Array.isArray(milestones) && milestones.length > 0 && (
          <div className={s.card}>
            <p className={s.cardTitle}><Icon d={ICONS.check} /> Milestone schedule</p>
            {milestones.map((m, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={s.milestone} key={i}>
                <span className={s.msIndex}>{i + 1}</span>
                <span className={s.msName}>{m.name || `Milestone ${i + 1}`}</span>
                <span className={s.msAmt}>{m.amount != null ? money2(m.amount, currency) : '—'}</span>
              </div>
            ))}
          </div>
        )}

        {/* Key terms */}
        <div className={s.card}>
          <p className={s.cardTitle}><Icon d={ICONS.shield} /> Key terms</p>
          <ul className={s.terms}>
            <li>Funds are held in escrow and released per the milestone schedule above, only after the client approves each milestone.</li>
            {quote && quote.warrantyDurationMonths ? (
              <li>Workmanship warranty: {quote.warrantyDurationMonths} months{quote.warrantyScope ? ` — ${quote.warrantyScope}` : ''}.</li>
            ) : null}
            {quote && quote.totalDurationDays ? (
              <li>Estimated duration: {quote.totalDurationDays} days from the contract start.</li>
            ) : null}
            <li>All work, payments and communication stay on the BestHand platform. Identities are released only at contract.</li>
            <li>This is a legally binding home-improvement contract governed by the applicable state rules snapshotted at signing.</li>
          </ul>
          {this.pdfHref() && (
            <p style={{ marginTop: 14 }}>
              <a className={s.pdfLink} href={this.pdfHref()} target="_blank" rel="noopener noreferrer">
                <Icon d={ICONS.doc} /> View the full contract PDF
              </a>
            </p>
          )}
        </div>

        {/* Cancellation window note */}
        {this.renderCancellationNote()}

        {/* Signature status (both parties) */}
        <div className={s.card}>
          <p className={s.cardTitle}><Icon d={ICONS.check} /> Signature status</p>
          <div className={s.partyGrid}>
            {this.renderPartyStatus('Client', contract.signedAtClient)}
            {this.renderPartyStatus('Company', contract.signedAtCompany)}
          </div>
        </div>

        {/* Active state OR signing panel */}
        {cancelled ? (
          <div className={s.card}>
            <p className={s.cardTitle}>Contract cancelled</p>
            <p className={s.subtitle}>
              {contract.cancellationReason || 'This contract was cancelled within the cancellation window.'}
            </p>
          </div>
        ) : fullySigned ? (
          this.renderActiveBanner()
        ) : (
          this.renderSignPanel()
        )}
      </div>
    );
  }

  renderPartyStatus(label, signedAt) {
    const signed = !!signedAt;
    return (
      <div className={`${s.partyCard} ${signed ? s.partyCardSigned : ''}`}>
        <span className={`${s.partyAvatar} ${signed ? s.partyAvatarSigned : ''}`}>
          <Icon d={signed ? ICONS.check : ICONS.clock} />
        </span>
        <div>
          <p className={s.partyName}>{label}</p>
          <p className={`${s.partyStatus} ${signed ? s.partyStatusSigned : ''}`}>
            {signed ? `Signed · ${fmtDateTime(signedAt)}` : 'Pending signature'}
          </p>
        </div>
      </div>
    );
  }

  renderCancellationNote() {
    const { contract } = this.state;
    const isClient = this.party === 'CLIENT';
    const deadline = contract.cancellationDeadline;
    const days = contract.cancellationDaysApplied;
    const withinWindow = deadline && new Date(deadline).getTime() > Date.now();
    const canCancel = isClient
      && withinWindow
      && ['IN_CANCELLATION_PERIOD', 'FULLY_SIGNED', 'SIGNED_CLIENT', 'SIGNED_COMPANY'].includes(contract.status);

    // Before the client signs, show the right-to-cancel as informational.
    return (
      <div className={s.cancelBox}>
        <Icon d={ICONS.clock} />
        <div style={{ flex: 1 }}>
          <p className={s.cancelTitle}>Your right to cancel</p>
          <p className={s.cancelBody}>
            {days
              ? `You have ${days} business day${days === 1 ? '' : 's'} after signing to cancel this contract at no cost`
              : 'You have a state-mandated cancellation window after signing to cancel this contract at no cost'}
            {deadline
              ? ` — through ${fmtDateTime(deadline)}.`
              : '. The exact deadline is set the moment the client signs.'}
            {' '}This right of rescission is required by your state’s home-improvement law.
          </p>
          {canCancel && !this.state.cancelOpen && (
            <button type="button" className={s.cancelLink} onClick={() => this.setState({ cancelOpen: true })}>
              Cancel within the window
            </button>
          )}
          {canCancel && this.state.cancelOpen && (
            <div style={{ marginTop: 10 }}>
              <input
                className={s.input}
                type="text"
                value={this.state.cancelReason}
                placeholder="Reason (optional)"
                onChange={(e) => this.setState({ cancelReason: e.target.value })}
              />
              <div className={s.actions}>
                <button type="button" className={s.btnGhost} disabled={this.state.cancelling}
                  onClick={() => this.setState({ cancelOpen: false })}>
                  Keep contract
                </button>
                <button type="button" className={s.btn} disabled={this.state.cancelling}
                  onClick={() => this.confirmCancel()}>
                  {this.state.cancelling ? 'Cancelling…' : 'Confirm cancellation'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderActiveBanner() {
    const { contract } = this.state;
    const inWindow = contract.status === 'IN_CANCELLATION_PERIOD';
    // Step #5 — escrow milestones open only once the contract is ACTIVE/COMPLETED
    // (the api seeds ProjectMilestone rows when the cron promotes the contract
    // past its cancellation window). During the window we keep the informational
    // banner only.
    const milestonesOpen = contract.status === 'ACTIVE' || contract.status === 'COMPLETED';
    const isClient = this.party === 'CLIENT';

    // ON-SESSION client-paid DEPOSIT: due once both parties have signed
    // (FULLY_SIGNED / IN_CANCELLATION_PERIOD) and before the contract is ACTIVE.
    // The client may pay it NOW (card or admin-enabled BNPL) to activate the
    // contract immediately, instead of waiting for the off-session auto-capture.
    const depositDue = isClient
      && !milestonesOpen
      && Number(contract.downPaymentAmount) > 0
      && !contract.depositPaidAt;

    return (
      <div>
        <div className={s.activeBanner}>
          <Icon d={ICONS.check} />
          <div>
            <p className={s.activeTitle}>
              {milestonesOpen
                ? 'Contract active — escrow milestones'
                : 'Contract fully signed — escrow milestones next'}
            </p>
            <p className={s.activeBody}>
              Both parties have signed.{' '}
              {inWindow
                ? `The contract is in its cancellation window${contract.cancellationDeadline ? ` until ${fmtDateTime(contract.cancellationDeadline)}` : ''}; once it elapses the project becomes active and the first escrow milestone is funded.`
                : 'The project is active — fund and approve milestones to release payment as the work is completed.'}
            </p>
          </div>
        </div>

        {/* ON-SESSION deposit checkout (card + admin-enabled BNPL). Paying now
            activates the contract immediately. */}
        {depositDue && (
          <LpDepositCheckout
            contractId={contract.id}
            downPaymentAmount={contract.downPaymentAmount}
            currency={contract.currency}
            onPaid={() => this.reloadContract()}
          />
        )}

        {/* CLIENT card-on-file — saved as the default card the company's
            OFF-SESSION "Start milestone" charge uses. Shown to the client
            from the moment the contract is signed onward. */}
        {isClient && (
          <LpCardOnFile
            title={milestonesOpen ? 'Card for milestone payments' : 'Save a card for milestones'}
            subtitle={milestonesOpen
              ? 'Each milestone is charged to this card as you approve the completed work. Update it any time.'
              : 'Save a card so each milestone after the deposit is charged automatically as you approve the work.'}
          />
        )}

        {milestonesOpen && (
          <ProjectMilestones
            viewerRole={this.props.viewerRole}
            contractId={contract.id}
          />
        )}
      </div>
    );
  }

  renderSignPanel() {
    const { contract, consent, reviewed, otpPhase, otp, sending, signing, signError, signNotice } = this.state;
    const alreadySigned = this.party === 'CLIENT' ? !!contract.signedAtClient : !!contract.signedAtCompany;

    if (alreadySigned) {
      return (
        <div className={s.card}>
          <p className={s.cardTitle}><Icon d={ICONS.check} /> You’ve signed</p>
          <p className={s.subtitle}>
            Your signature is recorded. We’re waiting on the other party to sign — you’ll be notified
            when the contract is fully signed and escrow milestones begin.
          </p>
        </div>
      );
    }

    return (
      <div className={s.card}>
        <p className={s.cardTitle}><Icon d={ICONS.doc} /> Sign the contract</p>

        {signError ? <div className={s.alertErr}>{signError}</div> : null}
        {signNotice ? <div className={s.alertNotice}>{signNotice}</div> : null}

        <label className={s.consentRow}>
          <input type="checkbox" checked={reviewed}
            onChange={(e) => this.setState({ reviewed: e.target.checked })} />
          <span className={s.consentText}>
            I have reviewed the full agreement, including the scope, line items, milestone schedule,
            total, and the contract PDF above.
          </span>
        </label>
        <label className={s.consentRow}>
          <input type="checkbox" checked={consent}
            onChange={(e) => this.setState({ consent: e.target.checked })} />
          <span className={s.consentText}>
            I consent to sign this contract electronically (ESIGN/UETA) and agree my e-signature is
            legally binding.
          </span>
        </label>

        {otpPhase === 'idle' ? (
          <div className={s.actions}>
            <button type="button" className={s.btn} disabled={!this.canSendOtp()}
              onClick={() => this.sendOtp()}>
              {sending ? 'Sending code…' : 'Send signing code'}
            </button>
          </div>
        ) : (
          <div>
            <div className={s.otpRow}>
              <div>
                <span className={s.fieldLabel}>Verification code</span>
                <input className={`${s.input} ${s.codeInput}`} type="text" inputMode="numeric" maxLength={6}
                  value={otp} placeholder="6-digit code"
                  onChange={(e) => this.setState({ otp: e.target.value.replace(/\D/g, '').slice(0, 6) })} />
              </div>
              <button type="button" className={s.btn} disabled={signing || (otp || '').length < 4}
                onClick={() => this.sign()}>
                {signing ? 'Signing…' : 'Sign contract'}
              </button>
            </div>
            <button type="button" className={s.linkBtn} style={{ marginTop: 12 }}
              disabled={sending} onClick={() => this.sendOtp()}>
              Resend code
            </button>
          </div>
        )}
        <p className={s.scrollNote}>
          We’ll text a 6-digit code to the mobile number on your account to confirm it’s really you.
        </p>
      </div>
    );
  }
}

export default withStyles(s)(ProjectContract);
